import React, { useState, useEffect } from 'react';

// Check sessionStorage synchronously before component renders
const getInitialShowState = () => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem('khan_preloader_shown') !== 'true';
  }
  return false;
};

const Preloader = ({ children }) => {
  // Initialize state based on sessionStorage - this runs only once
  const [showPreloader, setShowPreloader] = useState(getInitialShowState);
  const [fadeOut, setFadeOut] = useState(false);
  
  useEffect(() => {
    // If we shouldn't show preloader, do nothing
    if (!showPreloader) return;
    
    // Mark as shown immediately to prevent issues with StrictMode double-render
    sessionStorage.setItem('khan_preloader_shown', 'true');
    
    // Create timers
    let fadeTimer, hideTimer;
    
    // Start fade out after 2.5 seconds
    fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 2500);
    
    // Hide completely after fade animation (700ms transition)
    hideTimer = setTimeout(() => {
      setShowPreloader(false);
    }, 3200);
    
    // Cleanup function
    return () => {
      if (fadeTimer) clearTimeout(fadeTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [showPreloader]);
  
  // If preloader should not be shown, render children immediately
  if (!showPreloader) {
    return <>{children}</>;
  }
  
  return (
    <>
      {/* Preloader Overlay */}
      <div 
        className={`fixed inset-0 z-[9999] bg-[#0a0a0a] flex items-center justify-center transition-opacity duration-700 ease-out ${
          fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className="relative">
          {/* Glow effect behind text */}
          <div className="absolute inset-0 blur-3xl opacity-30 animate-pulse">
            <span className="font-serif text-6xl md:text-8xl tracking-[0.3em] text-white">
              KHAN
            </span>
          </div>
          
          {/* Main text with glow animation */}
          <h1 className="relative font-serif text-6xl md:text-8xl tracking-[0.3em] text-white khan-glow-text">
            KHAN
          </h1>
          
          {/* Subtle underline */}
          <div className="mt-6 flex justify-center">
            <div className="khan-underline h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
          </div>
        </div>
      </div>
      
      {/* Page content - fade in as preloader fades out */}
      <div className={`transition-opacity duration-700 ease-out ${fadeOut ? 'opacity-100' : 'opacity-0'}`}>
        {children}
      </div>
      
      {/* Animations */}
      <style>{`
        @keyframes khan-glow-pulse {
          0%, 100% {
            text-shadow: 
              0 0 20px rgba(255,255,255,0.2),
              0 0 40px rgba(255,255,255,0.15),
              0 0 60px rgba(255,255,255,0.1);
          }
          50% {
            text-shadow: 
              0 0 40px rgba(255,255,255,0.4),
              0 0 80px rgba(255,255,255,0.3),
              0 0 120px rgba(255,255,255,0.2);
          }
        }
        
        .khan-glow-text {
          text-shadow: 0 0 40px rgba(255,255,255,0.3), 0 0 80px rgba(255,255,255,0.2), 0 0 120px rgba(255,255,255,0.1);
          animation: khan-glow-pulse 2s ease-in-out infinite;
        }
        
        @keyframes khan-line-expand {
          0% { width: 0; opacity: 0; }
          50% { opacity: 1; }
          100% { width: 6rem; opacity: 0.6; }
        }
        
        .khan-underline {
          animation: khan-line-expand 1.5s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default Preloader;
